import React, { useEffect, useState } from 'react';
import { Text, StyleSheet, View, ScrollView } from 'react-native';
import { useTenantContext, useToastContext } from '../../../contexts';
import DeleteConfirmModal from '../../../modal/DeleteConfirmModal';
import Layout from '../../../components/layout/Layout';
import GenericButton from '../../../components/molecules/buttons/GenericButton';
import CredentialList from './credentials/CredentialList';
import GenericBack from '../../../components/molecules/buttons/GenericBack';
import { IUser } from '../../../types/user';
import SendUserCredModal from '../../../modal/SendUserCredModal';
import { useIssuerService } from '../../../services';
import ToastProvider from '../../../contexts/ToastContext';

interface IWalletUserDetailsProps {
  user: IUser;
  setVisible: (value: boolean) => void;
}

/**
 * WalletUserDetails component
 *
 * @param {*} { user, setVisible }
 * @return {*}
 */
const WalletUserDetails: React.FC<IWalletUserDetailsProps> = ({ user, setVisible }) => {
  const tenantContext = useTenantContext();
  const toast = useToastContext();
  const issuerService = useIssuerService();
  const [deleteUserModalVisible, setDeleteUserModalVisible] = useState(false);
  const [sendUserCredModalOpen, setSendUserCredModalOpen] = useState(false);

  const matchingCredentials = tenantContext.credentials?.filter(
    (cred) => cred.connectionId === user.connection_id
  );

  useEffect(() => {
    const getCredentials = async () => {
      if (user && user.connection_id) {
        await tenantContext.fetchCredentials(user.connection_id);
      }
    };
    if (!tenantContext.credentials) {
      getCredentials();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDeleteUserModal = () => {
    setDeleteUserModalVisible(true);
  };

  const closeDeleteUserModal = () => {
    setDeleteUserModalVisible(false);
  };

  const closeSendUserCredModal = () => {
    setSendUserCredModalOpen(false);
  };

  const deleteUser = async () => {
    try {
      // Delete user with wallet
      const response = await issuerService.deleteUser(user.email);
      if (response) {
        await tenantContext.fetchUsers();
        toast.showToast('User deleted successfully', 3000);
        setVisible(false);
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <ToastProvider>
      <Layout title="Issuer Application">
        <DeleteConfirmModal
          isVisible={deleteUserModalVisible}
          onClose={closeDeleteUserModal}
          onConfirm={deleteUser}
        />
        <SendUserCredModal
          isVisible={sendUserCredModalOpen}
          onClose={closeSendUserCredModal}
          user={user}
        />
        <ScrollView style={styles.container}>
          <GenericBack onPress={() => setVisible(false)} buttonStyle={styles.backButton} />

          {/* User Information Card */}
          <View style={styles.userCard}>
            <View style={styles.userHeader}>
              <View style={styles.avatarContainer}>
                <Text style={styles.avatarText}>
                  {user?.tenant_name?.charAt(0).toUpperCase() || 'U'}
                </Text>
              </View>
              <View style={styles.userInfo}>
                <Text style={styles.nameHeader}>{user?.tenant_name}</Text>
                <Text style={styles.emailText}>{user?.email}</Text>
                <Text style={styles.statusText}>Active Wallet User</Text>
              </View>
            </View>

            <View style={styles.buttonContainer}>
              <GenericButton
                title={'Issue Credential'}
                onPress={() => setSendUserCredModalOpen(true)}
                buttonStyle={styles.primaryButton}
                textStyle={styles.buttonText}
                isPrimary={true}
              />
              <GenericButton
                title={'Delete Wallet'}
                onPress={openDeleteUserModal}
                buttonStyle={styles.deleteButton}
                textStyle={styles.buttonText}
                isPrimary={false}
              />
            </View>
          </View>

          {/* Wallet Details Card */}
          <View style={styles.detailsCard}>
            <Text style={styles.sectionTitle}>Wallet Details</Text>
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Tenant ID:</Text>
              <Text style={styles.detailValue}>{user.tenant_id}</Text>
            </View>
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Wallet ID:</Text>
              <Text style={styles.detailValue}>{user?.wallet_id}</Text>
            </View>
          </View>

          {/* Credentials Section */}
          <View style={styles.credentialsCard}>
            <View style={styles.credentialsHeader}>
              <Text style={styles.sectionTitle}>
                Credentials ({matchingCredentials?.length || 0})
              </Text>
            </View>
            <CredentialList
              user={user}
              credentials={matchingCredentials || []}
              setSendUserCredModalOpen={setSendUserCredModalOpen}
            />
          </View>
        </ScrollView>
      </Layout>
    </ToastProvider>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: '60%'
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginLeft: 16,
    marginBottom: 8,
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    alignSelf: 'flex-start'
  },
  userCard: {
    backgroundColor: '#f8f9fa',
    marginHorizontal: 16,
    marginBottom: 16,
    borderRadius: 12,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2
    },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    borderLeftWidth: 4,
    borderLeftColor: '#28a745'
  },
  userHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20
  },
  avatarContainer: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: '#28a745',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16
  },
  avatarText: {
    color: '#ffffff',
    fontSize: 24,
    fontWeight: '600'
  },
  userInfo: {
    flex: 1
  },
  nameHeader: {
    fontSize: 24,
    fontWeight: '700',
    color: '#212529',
    marginBottom: 4
  },
  emailText: {
    fontSize: 16,
    fontWeight: '400',
    color: '#6c757d',
    marginBottom: 4
  },
  statusText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#28a745'
  },
  buttonContainer: {
    flexDirection: 'row',
    gap: 12
  },
  primaryButton: {
    flex: 1,
    height: 44,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#007bff'
  },
  deleteButton: {
    flex: 1,
    height: 44,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#dc3545'
  },
  buttonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff'
  },
  detailsCard: {
    backgroundColor: '#f8f9fa',
    marginHorizontal: 16,
    marginBottom: 16,
    borderRadius: 12,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2
    },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#495057',
    marginBottom: 16
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8
  },
  detailLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#6c757d'
  },
  detailValue: {
    fontSize: 14,
    fontWeight: '400',
    color: '#212529',
    flex: 1,
    textAlign: 'right'
  },
  credentialsCard: {
    backgroundColor: '#ffffff',
    marginHorizontal: 16,
    marginBottom: 16,
    borderRadius: 12,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2
    },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3
  },
  credentialsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16
  }
});

export default WalletUserDetails;
